import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import _ from 'lodash';
import { Col } from '@pickit/pickit-grid-system';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { CHIPGUTTER, ChipProps } from './types';
import { Label, ChipContainer, Input } from './styles';

/**
 *
 * @param isMultipleSelection boolean - If 'true' initialValues of type ['value1', 'value2',.....]
 *
 */

const Chip = ({
  onChange,
  input,
  testId,
  disabled,
  options = [],
  keyProperty = 'id',
  showProperty = 'name',
  isMultpleSelection = false,
  amountPerRow = 4,
  isValueObject,
}: ChipProps) => {
  const { actualTheme } = useContext(ThemeContext);
  const inputRef: React.Ref<HTMLInputElement> = useRef(null);
  const inputName = useMemo(() => input?.name, [input?.name]);
  const [optionsState, setOptionsState] = useState([]);

  const handleLabelClick = useCallback(
    (valueChanged: any) => () => {
      let opt = [...optionsState];
      const keyValue = _.get(valueChanged, keyProperty);
      const singleValue = isValueObject ? valueChanged : keyValue;
      const index = opt.findIndex((ob) => _.get(ob, keyProperty) === keyValue);
      let multipleValue = (input.value as Array<any>) || [];

      if (isMultpleSelection) {
        const keyValueIndex = multipleValue.findIndex((v) =>
          isValueObject ? v[keyProperty] === keyValue : v === keyValue,
        );
        if (keyValueIndex !== -1) {
          multipleValue.splice(keyValueIndex, 1);
        } else multipleValue = [...multipleValue, isValueObject ? valueChanged : keyValue];
      } else {
        opt = opt.map((ob: any, i: number) => ({
          ...ob,
          selected: i === index ? ob.selected : false,
        }));
      }
      opt[index].selected = !opt[index].selected;
      setOptionsState(opt);

      if (onChange) {
        onChange(isValueObject ? valueChanged : valueChanged[keyProperty]);
      }
      if (input) {
        input.onChange(isMultpleSelection ? multipleValue : singleValue);
      }
    },
    [optionsState, keyProperty, input, isMultpleSelection, onChange, isValueObject],
  );

  useEffect(() => {
    setOptionsState(
      options.map((ob: any) => {
        let selected = false;
        if (isMultpleSelection && Array.isArray(input.value)) {
          const keyValueIndex = input.value.findIndex((v: any) =>
            isValueObject
              ? _.get(v, keyProperty) === _.get(ob, keyProperty)
              : v === _.get(ob, keyProperty),
          );
          selected = keyValueIndex !== -1;
        } else {
          selected = isValueObject
            ? _.get(input.value, keyProperty) === _.get(ob, keyProperty)
            : input.value === _.get(ob, keyProperty);
        }
        return {
          ...ob,
          selected,
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, keyProperty]);

  return (
    <ThemeProvider theme={actualTheme}>
      <ChipContainer data-testid={testId} gutter={CHIPGUTTER} cols={amountPerRow}>
        {optionsState.map((option: any, index: number) => (
          <Col xxs={1}>
            <Label
              role="presentation"
              onClick={handleLabelClick(option)}
              data-testid={`${testId}-option-${index}`}
              id="label-test"
              isSelected={optionsState[index].selected}
              isSecondRowElement={index >= amountPerRow}
            >
              {option[showProperty]}
            </Label>
          </Col>
        ))}
      </ChipContainer>
      <Input
        ref={inputRef}
        name={inputName}
        type="text"
        data-testid={`${testId}-input`}
        disabled={disabled}
        id="input"
      />
    </ThemeProvider>
  );
};

export default Chip;
